import styles from './login-form.module.css'

import React, { useState } from 'react'
import type { Location } from 'react-router-dom'

import api from '@disco/api/server'
import SignupForm from '@disco/accounts/components/auth/SignupForm'
import type { SignupData } from '@disco/accounts/components/auth/SignupForm'

import LoginForm from 'components/common/login-form'
import Separator from 'components/common/login-form/separator'
import { Box, Button, Flex, Heading, Text } from 'components/ds'
import DiscoIcon from 'components/ui/icon/disco-icon'

import type { AddToDiscoUser } from '../types'

type Props = {
  user: AddToDiscoUser
  showSignIn: boolean
  onShowSignIn: () => void
  onLoginUser: (...args: Array<any>) => any
  onForgotPassword: (email: string, returnUrl: string) => Promise<any>
  onGetSignUpUrl: () => Promise<string>
  onEmbeddedSignupChange?: (showing: boolean) => void
}

const AddToDiscoLoginForm = ({
  user,
  showSignIn,
  onShowSignIn,
  onLoginUser,
  onForgotPassword,
  onGetSignUpUrl,
  onEmbeddedSignupChange,
}: Props): React.ReactNode => {
  const [showSignupForm, setShowSignupForm] = useState(false)
  const [signupComplete, setSignupComplete] = useState(false)
  const [signupEmail, setSignupEmail] = useState<string | undefined>(undefined)
  const [stylesLoading, setStylesLoading] = useState(false)

  const handleGetStarted = async (e: React.FormEvent) => {
    e.preventDefault()
    setStylesLoading(true)
    try {
      await import('@disco/accounts/styles/tailwind.css')
    } finally {
      setStylesLoading(false)
      setShowSignupForm(true)
      onEmbeddedSignupChange?.(true)
    }
  }

  const handleSignupSuccess = (data: SignupData) => {
    setSignupEmail(data.email as string)
    setShowSignupForm(false)
    setSignupComplete(true)
    onEmbeddedSignupChange?.(false)
  }

  if (showSignIn) {
    return (
      <Box width="312px">
        <LoginForm
          user={user}
          location={window.location as unknown as Location}
          heading="Sign in"
          message="This content will be added to your DISCO"
          onLoginUser={onLoginUser}
          onForgotPassword={onForgotPassword}
          onSamlDiscovery={async email =>
            api.samlLogin({
              username: email.trim(),
              redirect_uri: window.location.href,
            })
          }
          onGetSignupUrl={onGetSignUpUrl}
        />
      </Box>
    )
  }

  if (showSignupForm) {
    return <SignupForm embedded onSuccess={handleSignupSuccess} />
  }

  if (signupComplete) {
    return (
      <Flex direction="column" gap="3" width="312px" align="center">
        <Heading size="5" align="center" m="0">
          Check your email
        </Heading>
        <Text size="2" color="gray" align="center">
          Our team is reviewing your signup and will be in touch via{' '}
          {signupEmail && <strong>{signupEmail}</strong>}.
        </Text>
      </Flex>
    )
  }

  return (
    <Flex direction="column" gap="3" width="312px">
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="4">
          <Heading size="6" m="0">
            Save to DISCO
          </Heading>
          <Text size="2" color="gray">
            These tracks will be saved as a new playlist in your DISCO account.
          </Text>
        </Flex>
        <form onSubmit={handleGetStarted}>
          <Button
            type="submit"
            size="3"
            color="gray"
            highContrast
            radius="medium"
            className={styles.FullWidthButton}
            data-testid="signup-get-started-button"
            disabled={stylesLoading}
          >
            <DiscoIcon />
            Sign up to DISCO
          </Button>
        </form>
      </Flex>

      <Separator or />

      <Flex direction="column" gap="3" align="center">
        <Button
          type="button"
          size="3"
          variant="soft"
          color="gray"
          className={styles.FullWidthButton}
          onClick={onShowSignIn}
        >
          Sign in
        </Button>
      </Flex>
    </Flex>
  )
}

export default AddToDiscoLoginForm
